import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, EmptyState } from '@indipia/ui-core';
import type { ReportCard } from '../api/customerHome';
import { CustomerImg } from './PreviewContext';
import { RequestBlock } from './RequestBlock';
import { fmtDateDow, fmtMdTime, parseDtm } from './format';

function ChatIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <polyline points="12 7 12 12 15 14" />
    </svg>
  );
}

/** 카드 정렬 — 작업일 최신순, 같으면 발송일시 최신순(REQ-5-16). */
export function sortReportCards(cards: ReportCard[]): ReportCard[] {
  return cards
    .slice()
    .sort((a, b) => b.workDate.localeCompare(a.workDate) || b.sentDtm.localeCompare(a.sentDtm));
}

interface Props {
  cards: ReportCard[];
  lastVisitedDtm: string | null;
  /** 알림톡으로 도착한 카드 — 진입 시 그 자리로 스크롤하고 1.5초간 강조한다. */
  highlightCrItemId: number | null;
  onOpenPhoto: (card: ReportCard, photoIndex: number) => void;
}

/**
 * 현장리포트 탭 — 발송 스냅샷 카드(작업일·공종·사장님 메시지·사진·발송일시). 발송 후 수정되지 않는다.
 * 직전 방문 이후 발송된 카드는 파란 테두리와 NEW 태그로 구분하고, 이 표시는 이번 방문 동안 유지된다.
 */
export function ReportTab({ cards, lastVisitedDtm, highlightCrItemId, onOpenPhoto }: Props) {
  const sorted = useMemo(() => sortReportCards(cards), [cards]);
  const since = lastVisitedDtm ? parseDtm(lastVisitedDtm).getTime() : null;
  const [flash, setFlash] = useState<number | null>(null);
  const targetRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (highlightCrItemId == null) return;
    setFlash(highlightCrItemId);
    targetRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setFlash(null), 1500);
    return () => clearTimeout(t);
  }, [highlightCrItemId]);

  if (sorted.length === 0) {
    return <EmptyState title="공유된 현장리포트가 없습니다" description="사장님이 작업 소식을 보내면 여기에 표시됩니다." />;
  }

  return (
    <div className="flex flex-col gap-05">
      {sorted.map((card) => {
        const isNew = since !== null && parseDtm(card.sentDtm).getTime() > since;
        const photos = card.photos.slice().sort((a, b) => a.photoSeq - b.photoSeq);
        const flashing = flash === card.crItemId;
        return (
          <article
            key={card.crItemId}
            ref={card.crItemId === highlightCrItemId ? targetRef : undefined}
            className="overflow-hidden rounded-xl border bg-surface-raised transition-shadow"
            style={
              isNew || flashing
                ? { borderColor: 'var(--status-info-200)', boxShadow: '0 0 0 var(--spacing-01) var(--color-infoSubtle)' }
                : { borderColor: 'var(--shell-border)' }
            }
          >
            <header className="flex items-center gap-03 border-b border-border px-05 py-04">
              <span
                aria-hidden="true"
                className="h-03 w-03 shrink-0 rounded-full"
                style={{ background: isNew ? 'var(--color-info)' : 'var(--shell-hint)' }}
              />
              <div className="text-caption font-bold text-text-secondary">{fmtDateDow(card.workDate)}</div>
              {isNew && (
                <Badge status="accent">
                  NEW
                </Badge>
              )}
              <div className="flex-1" />
              <div className="flex flex-wrap justify-end gap-02">
                {card.workTypeNames.map((name) => (
                  <span key={name} className="whitespace-nowrap rounded-sm border border-border bg-surface px-03 py-01 text-caption font-bold text-text-secondary">
                    {name}
                  </span>
                ))}
              </div>
            </header>

            <div className="px-05 py-04">
              {card.customerMemo && (
                <div
                  className="rounded-lg border px-04 py-03"
                  style={{ background: 'var(--shell-point-subtle)', borderColor: 'var(--shell-point-subtle)' }}
                >
                  <div className="mb-02 flex items-center gap-02 text-caption font-bold" style={{ color: 'var(--shell-point)' }}>
                    <ChatIcon />
                    사장님 메시지
                  </div>
                  <p className="whitespace-pre-line text-caption text-text">{card.customerMemo}</p>
                </div>
              )}
              {photos.length > 0 && (
                <div className={['grid grid-cols-4 gap-03', card.customerMemo ? 'mt-04' : ''].join(' ')}>
                  {photos.map((p, i) => (
                    <button
                      key={p.photoSeq}
                      type="button"
                      aria-label={`${fmtDateDow(card.workDate)} 현장리포트 사진 ${i + 1} 크게 보기`}
                      onClick={() => onOpenPhoto(card, i)}
                      className="aspect-square overflow-hidden rounded-md bg-surface active:scale-95"
                    >
                      <CustomerImg src={p.url} alt="" loading="lazy" draggable={false} className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
              <RequestBlock
                requests={card.requests}
                className={card.customerMemo || photos.length > 0 ? 'mt-04' : ''}
              />
            </div>

            <footer className="flex items-center justify-end gap-02 border-t border-border px-05 pb-04 pt-03 text-caption text-text-disabled">
              <ClockIcon />
              {fmtMdTime(card.sentDtm)} 발송
            </footer>
          </article>
        );
      })}
    </div>
  );
}
